import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import client, { imgUrl } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import MiniAd from "@/components/MiniAd";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { cycleIndexFor, editionLabel } from "@/lib/issue";

export const archiveLabel = (e) =>
  e.issue ? editionLabel(cycleIndexFor(e.issue + "T12:00:00Z")) : "The Test Edition · August 2026";

export default function Archive() {
  const [editions, setEditions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    client.get("/archive/editions").then((r) => setEditions(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <MagazineLayout>
      <Seo
        title="The Archive — S/ME"
        description="Every past edition of S/ME, kept whole. Open any back issue and read it cover to cover — the cover star, the spotlights, the interviews and the newsroom, exactly as published."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "The S/ME Archive",
          description: "Back issues of S/ME, the UK's online SME magazine — each edition preserved as a complete magazine.",
          isPartOf: { "@type": "Periodical", name: "S/ME", alternateName: "See Me" },
        }}
      />
      <div className="pt-[68px] bg-cream min-h-screen" data-testid="archive-page">
        <header className="px-5 sm:px-8 pt-16 sm:pt-24 pb-10 max-w-[1300px] mx-auto">
          <p className="overline text-gloss-silver mb-4">Back Issues</p>
          <h1 className="font-serif text-5xl sm:text-7xl tracking-tight leading-none">The Archive</h1>
          <p className="font-sans text-lg text-ink/70 mt-5 max-w-xl">
            Every edition, kept whole. When a new edition goes live, the previous one moves here — open it and read it cover to cover, exactly as it ran.
          </p>
        </header>
        <div className="px-5 sm:px-8 max-w-[1300px] mx-auto mb-10"><MiniAd variant="banner" mode="image" space="archive_banner" /></div>
        <section className="px-5 sm:px-8 pb-28 max-w-[1300px] mx-auto">
          {loaded && editions.length === 0 ? (
            <p className="font-sans text-ink/50 py-20 text-center border-t border-ink/20" data-testid="archive-empty">
              No back issues yet — everything is in the current edition. The archive fills automatically when the next edition goes live.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 border-t border-ink/20 pt-10">
              {editions.map((e, i) => <EditionCard key={e.key} e={e} delay={i * 0.07} />)}
            </div>
          )}
        </section>
      </div>
    </MagazineLayout>
  );
}

function EditionCard({ e, delay }) {
  const cover = e.cover;
  return (
    <Reveal delay={delay}>
      <Link to={`/archive/${e.key}`} className="group block" data-testid={`archive-edition-card-${e.key}`}>
        <div className="relative overflow-hidden aspect-[3/4] bg-ink ed-frame">
          {cover ? (
            <img src={imgUrl(cover)} alt={cover.title} className="w-full h-full object-cover object-top ed-grade transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-serif text-cream/20 text-7xl tracking-tighter">S/ME</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/30" />
          {!cover && (
            <div className="absolute top-0 inset-x-0 p-5 flex items-start justify-between">
              <span className="font-serif text-cream text-3xl leading-none tracking-tight">S/ME</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gloss-gold mt-1">Back Issue</span>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
            <p className="overline text-gloss-gold">{archiveLabel(e)}</p>
            {cover && (
              <h2 className="font-serif text-2xl sm:text-3xl text-cream tracking-tight leading-tight mt-2 line-clamp-3">{cover.title}</h2>
            )}
            <p className="font-sans text-sm text-cream/70 mt-2">
              {cover?.member_name ? `Cover · ${cover.member_name}` : "From the S/ME desk"} · {e.count} {e.count === 1 ? "story" : "stories"}
            </p>
            <span className="inline-flex items-center gap-2 mt-4 overline text-cream group-hover:text-gloss-gold group-hover:gap-3 transition-all">
              Open this edition <ArrowUpRight size={13} />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}
